import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, ButtonGroup, Checkbox, Chip, CircularProgress, FormControlLabel, Radio, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, List, ListItem, ListItemButton, Tooltip, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import _ from "lodash";
import moment from 'moment';
import { Autocomplete, GoogleMap, Polygon, Polyline, Marker, OverlayView } from "@react-google-maps/api";
import Div from "@jumbo/shared/Div";
import { convexHull, geoCoords } from "app/utils/appHelpers";
import customerServices from 'app/services/customer-services';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import PrimaryAddressChip from 'app/shared/widgets/PrimaryAddressChip';
import TrashIconButton from 'app/shared/widgets/TrashIconButton';
import { useSnackbar } from 'notistack';

const DEFAULT_CENTER = { lat: -33.5136445, lng: -70.7323127 };
// selectedId de la geocerca recien dibujada en el instructivo, que aun no existe en el backend.
const NEW_ID = 'new';
// selectedId de la direccion que la parada usa solo para su pedido: no esta entre las del cliente.
const ORDER_ID = 'order';

// Desglosa el place de Google en las columnas de Location. Se manda ya
// desglosado y no como texto: el backend NO re-geocodifica, porque
// formatted_address es una etiqueta para leer y al pasarla por HERE volvía
// otro lugar (ver Location.geocode_for_geofence).
export const googlePlaceToAddress = (place) => {
    const part = (type) => _.find(place?.address_components, (c) => _.includes(c.types, type))
    const country = part('country')
    const region = part('administrative_area_level_1')
    // En Chile la comuna viene en administrative_area_level_3; `locality`
    // dice "Santiago" para direcciones de cualquier comuna del Gran Santiago.
    const commune = part('administrative_area_level_3') || part('locality')
    // Location#short_address es "street street_number" sin chequear nil, asi
    // que un lugar sin calle (una comuna, o un POI como Costanera Center, que
    // no traen `route`) se veia como una fila en blanco en la lista. Cuando no
    // hay calle se usa el nombre del lugar, y sin numero: el numero es de la
    // calle, pegarselo al nombre daria "Cenco Costanera 2425".
    const route = part('route')

    return {
        address: place?.formatted_address || place?.name || null,
        street: route?.long_name || place?.name || null,
        street_number: route ? (part('street_number')?.long_name || null) : null,
        city: commune?.long_name || null,
        county: part('administrative_area_level_2')?.long_name || null,
        state: region?.long_name || null,
        state_code: region?.short_name || null,
        postal_code: part('postal_code')?.long_name || null,
        country: country?.long_name || null,
        country_code: country?.short_name || null,
        map_place_id: place?.place_id || null,
        lat: place?.geometry?.location?.lat(),
        lng: place?.geometry?.location?.lng(),
    }
}

// Donde centrar el mapa para una direccion: la primera esquina de su geocerca o, si no
// tiene geocerca dibujada (las que llegan geocodificadas desde el ERP), su lat/lng.
const pointOf = (addr) => {
    const firstPoint = addr?.path && _.split(addr.path, ';')[0]
    if (firstPoint) {
        return {
            lat: _.toNumber(_.split(firstPoint, ',')[0]),
            lng: _.toNumber(_.split(firstPoint, ',')[1])
        }
    }
    if (addr?.lat && addr?.lng) return { lat: _.toNumber(addr.lat), lng: _.toNumber(addr.lng) }
    return null
}

// Geocerca por defecto para una direccion buscada: hexagono de 50 m de radio, el mismo
// tamano que arma el backend en Location.hexagon_polygon. Asi se puede guardar sin dibujar.
const AUTO_GEOFENCE_RADIUS_M = 50
const hexagonAround = ({ lat, lng }) => _.map(_.range(6), (i) => {
    const angle = Math.PI / 3 * i
    const dLat = (AUTO_GEOFENCE_RADIUS_M * Math.cos(angle)) / 111320
    const dLng = (AUTO_GEOFENCE_RADIUS_M * Math.sin(angle)) / (111320 * Math.cos(lat * Math.PI / 180))
    return { lat: lat + dLat, lng: lng + dLng }
})

// "lat,lng;lat,lng;", el mismo formato que armaba el DrawingManager.
const toPathString = (points) => points
    .map((p) => new window.google.maps.LatLng(p.lat, p.lng).toUrlValue(6) + ";")
    .join("")

/**
 * Direcciones y geocercas de un cliente: agregar otra, hacerla principal o borrarla.
 * Se usa en Clientes y en el instructivo (icono de mapa de cada parada).
 *
 * No carga el script de Google Maps: lo recibe ya cargado (`isLoaded`) de la pantalla que
 * lo abre, porque cada pantalla lo carga con su lista de librerias y el cargador no admite
 * dos listas distintas en la misma pagina.
 *
 * `stop` es opcional y solo lo pasa el instructivo. Con el nada se guarda al momento: se
 * elige la direccion de la parada (una del cliente, o una nueva buscada ahi) y se aplica con
 * "Guardar". Dos checkboxes deciden a que se aplica: "Usar para este pedido" (marcado de
 * entrada) cambia la parada, y "Asignar direccion al cliente" la deja como su principal.
 * Una direccion nueva sin "Asignar direccion al cliente" queda solo en el pedido: se le pasa a
 * onChoose como { geoPath, address } y no se suma a las direcciones del cliente.
 *   stop = { locationId, location, onChoose: (address) => Promise, label }
 */
const LocationDialog = ({ open, onClose, customer, isLoaded, onChanged = () => {}, stop = null }) => {
    const [loading, setLoading] = useState(false)
    const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER)
    // Direcciones del cliente (cada una con su geocerca). Reemplaza la antigua
    // geocerca única por cliente.
    const [customerAddresses, setCustomerAddresses] = useState([])
    const [activeAddressId, setActiveAddressId] = useState(null)
    // Modo "dibujar una nueva geocerca" y sus vértices en curso (reemplazo del
    // DrawingManager, que se dibuja click a click).
    const [isAddingGeofence, setIsAddingGeofence] = useState(false)
    const [geoDrawPath, setGeoDrawPath] = useState([])
    const [searchResult, setSearchResult] = useState(null)
    const [showPins, setShowPins] = useState(true)
    const [receiptsPins, setReceiptsPins] = useState([])
    const [tempPin, setTempPin] = useState(null)
    // Direccion elegida en "Buscar ubicacion". Es la que se guarda junto a la
    // geocerca que se dibuje a continuacion.
    const [searchedAddress, setSearchedAddress] = useState(null)
    // Dirección pendiente de borrar, o null. Borrarla se lleva también su geocerca.
    const [confirmRemoveAddress, setConfirmRemoveAddress] = useState(null)
    // Solo con `stop`: la direccion elegida para la parada (id de una del cliente, o NEW_ID
    // para la geocerca recien dibujada) y esa geocerca, que no se guarda hasta "Guardar".
    const [selectedId, setSelectedId] = useState(null)
    const [pendingAddress, setPendingAddress] = useState(null)
    const [saving, setSaving] = useState(false)
    const [markPrimary, setMarkPrimary] = useState(false)
    const [useForStop, setUseForStop] = useState(true)
    const { enqueueSnackbar } = useSnackbar();

    // El input de "Buscar ubicacion" lo maneja el Autocomplete de Google y no
    // React, asi que para vaciarlo hay que tocar el nodo.
    const searchInputRef = useRef(null);

    const clearLocationSearch = () => {
        setSearchedAddress(null)
        setTempPin(null)
        if (searchInputRef.current) { searchInputRef.current.value = '' }
    }

    // Al abrir se leen las direcciones frescas: en el instructivo el cliente viene del viaje,
    // que no se recarga al agregar una geocerca.
    useEffect(() => {
        if (!open || !customer?.id) return

        setLoading(true)
        setCustomerAddresses([])
        setActiveAddressId(null)
        setIsAddingGeofence(false)
        setGeoDrawPath([])
        setReceiptsPins([])
        setSelectedId(null)
        setPendingAddress(null)
        setMarkPrimary(false)
        setUseForStop(true)
        clearLocationSearch()

        customerServices.getCustomer(customer.id, "include=location,recipients")
            .then(response => {
                const addresses = response?.addresses || []
                setCustomerAddresses(addresses)
                const stopMatch = stop && _.find(addresses, { location_id: stop.locationId })
                if (stop) setSelectedId(stopMatch?.id || (stop.location ? ORDER_ID : null))

                // En el instructivo se centra en la direccion que usa la parada.
                const target = stopMatch
                    || (stop?.location && !stopMatch ? stop.location : null)
                    || _.find(addresses, 'primary')
                    || addresses[0]
                setMapCenter(pointOf(target) || DEFAULT_CENTER)

                return customerServices.getReceiptsPins(customer.id)
                    .then(resp => setReceiptsPins(resp || []))
            })
            .catch(function (error) {})
            .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, customer?.id])

    const applyCustomerResponse = (response) => {
        setCustomerAddresses(response?.addresses || [])
        onChanged(response)
    }

    function onPlaceChanged() {
        if (searchResult == null) return
        const place = searchResult.getPlace();
        if(place?.geometry?.location?.lat()) {
            const latlng = {
                lat: place?.geometry?.location?.lat(),
                lng: place?.geometry?.location?.lng(),
            }
            setTempPin(latlng)
            setMapCenter(latlng)
            const address = googlePlaceToAddress(place)
            if (stop && !isAddingGeofence) {
                // En el instructivo la direccion buscada queda elegida al tiro, con una
                // geocerca automatica que se puede redibujar con "Dibujar a mano".
                const hull = hexagonAround(latlng)
                setPendingAddress({ path: toPathString(hull), hull, address, auto: true })
                setSelectedId(NEW_ID)
                setActiveAddressId(null)
            } else {
                setSearchedAddress(address)
            }
        }
    }

    // Agrega una nueva dirección (geocerca) al cliente.
    const addNewAddress = (strGeo) => {
        if (!customer || !strGeo) return
        setLoading(true)
        // Sin address el backend cae al reverse geocoding de la primera esquina
        // del poligono, que es como funcionaba antes.
        customerServices.addCustomerAddress(customer.id, { geoPath: strGeo, address: searchedAddress })
            .then(applyCustomerResponse)
            .catch(function (error) {})
            .finally(() => {
                setLoading(false)
                setIsAddingGeofence(false)
                setGeoDrawPath([])
                // La proxima geocerca arranca limpia: sin esto reusaria la
                // direccion de la anterior.
                clearLocationSearch()
            })
    }

    const makeAddressPrimary = (addressId) => {
        if (!customer) return
        setLoading(true)
        customerServices.setPrimaryAddress(customer.id, addressId)
            .then(applyCustomerResponse)
            .catch(function (error) {})
            .finally(() => setLoading(false))
    }

    const removeCustomerAddress = (addressId) => {
        if (!customer || !addressId) return
        setLoading(true)
        customerServices.deleteCustomerAddress(customer.id, addressId)
            .then((response) => {
                applyCustomerResponse(response)
                setActiveAddressId(null)
            })
            .catch(function (error) {})
            .finally(() => setLoading(false))
    }

    // Direccion con la que la parada ya esta planificada (null si no es de la lista).
    // La direccion actual de la parada cuando es solo de su pedido (no esta entre las del cliente).
    const orderOnlyLocation = (stop?.location && !loading && !_.find(customerAddresses, { location_id: stop.locationId }))
        ? stop.location : null
    const stopAddressId = stop
        ? (_.find(customerAddresses, { location_id: stop.locationId })?.id || (orderOnlyLocation ? ORDER_ID : null))
        : null
    const selectedAddress = (selectedId === NEW_ID || selectedId === ORDER_ID) ? null : _.find(customerAddresses, { id: selectedId })
    const stopDirty = selectedId === NEW_ID || (selectedId !== null && selectedId !== stopAddressId)
    const canSaveAsPrimary = selectedId === NEW_ID || Boolean(selectedAddress && !selectedAddress.primary)
    const wantsPrimary = markPrimary && canSaveAsPrimary
    const appliesToStop = useForStop && stopDirty
    const canSaveStop = !saving && !isAddingGeofence && selectedId !== null && (appliesToStop || wantsPrimary)

    // Por que no se puede guardar todavia, o que va a pasar al guardar.
    const stopHint = (() => {
        if (isAddingGeofence) return 'Termina o cancela el dibujo de la geocerca para poder guardar.'
        if (selectedId === null) return 'Busca una dirección nueva o elige una de la lista.'
        if (!useForStop && !wantsPrimary) return 'Marca para qué se usa: este pedido, el cliente o los dos.'
        if (useForStop && !stopDirty && !wantsPrimary) return 'Dirección actual del pedido.'
        if (!appliesToStop) return 'Se dejará como dirección principal del cliente.'
        if (wantsPrimary) return 'El pedido cambiará a esta dirección y quedará como principal del cliente, luego debes volver a optimizar la ruta.'
        if (selectedId === NEW_ID) return 'El pedido cambiará a esta dirección (solo para este pedido), luego debes volver a optimizar la ruta.'
        return 'El pedido cambiará a esta dirección, luego debes volver a optimizar la ruta.'
    })()

    // Guarda lo elegido para la parada. La geocerca nueva se crea en el cliente solo si se marco
    // "Asignar direccion al cliente"; si no, queda solo en el pedido.
    const saveForStop = async (asPrimary) => {
        if (!customer || !stop || selectedId === null) return
        setSaving(true)
        try {
            if (selectedId === NEW_ID && !asPrimary) {
                // onChoose avisa, cierra el dialogo y recarga el instructivo.
                await stop.onChoose({ geoPath: pendingAddress.path, address: pendingAddress.address })
                return
            }
            let addr = selectedAddress
            let response = null
            if (selectedId === NEW_ID) {
                const previousIds = _.map(customerAddresses, 'id')
                response = await customerServices.addCustomerAddress(customer.id, { geoPath: pendingAddress.path, address: pendingAddress.address })
                addr = _.find(response?.addresses, (a) => !_.includes(previousIds, a.id))
                    || _.find(response?.addresses, { location_id: response?.location_id })
                setCustomerAddresses(response?.addresses || [])
                setPendingAddress(null)
                setSelectedId(addr?.id || null)
            }
            if (!addr) throw new Error('No se pudo guardar la geocerca')
            if (asPrimary && !addr.primary) {
                response = await customerServices.setPrimaryAddress(customer.id, addr.id)
                setCustomerAddresses(response?.addresses || [])
            }
            if (useForStop && addr.location_id !== stop.locationId) {
                // onChoose avisa, cierra el dialogo y recarga el instructivo.
                await stop.onChoose(addr)
            } else {
                if (response) onChanged(response)
                enqueueSnackbar('Dirección principal del cliente actualizada', { variant: 'success' })
                handleClose()
            }
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.error || err?.message || 'No se pudo guardar la dirección', { variant: 'error' })
        } finally {
            setSaving(false)
        }
    }

    const startAddGeofence = () => {
        setGeoDrawPath([])
        // Redibujar la direccion buscada conserva su direccion: la geocerca a mano solo
        // reemplaza al hexagono automatico.
        if (pendingAddress?.address) setSearchedAddress(pendingAddress.address)
        setIsAddingGeofence(true)
    }

    const handleGeofenceMapClick = (e) => {
        if (!isAddingGeofence || !e?.latLng) return;
        setGeoDrawPath((prev) => [...prev, { lat: e.latLng.lat(), lng: e.latLng.lng() }]);
    };

    // Cierra el polígono (envolvente convexa de los clicks).
    const finishGeofence = () => {
        const hull = convexHull(geoDrawPath);
        if (hull.length < 3) return;
        const coordStr = toPathString(hull);
        if (stop) {
            // En el instructivo queda como borrador elegido, y se guarda con los botones de abajo.
            setPendingAddress({ path: coordStr, hull, address: searchedAddress })
            setSelectedId(NEW_ID)
            setIsAddingGeofence(false)
            clearLocationSearch()
        } else {
            addNewAddress(coordStr)
        }
        setGeoDrawPath([]);
    };

    const cancelGeofence = () => {
        setGeoDrawPath([]);
        setIsAddingGeofence(false);
        // Si habia una direccion buscada, sigue elegida con su geocerca automatica.
        if (pendingAddress) setSearchedAddress(null)
    };

    const setCentreOnCustomer = (target = null) => {
        // target explícito (al seleccionar una dirección) evita depender del
        // activeAddressId aún no aplicado por setState (que obligaba a doble click).
        const addr = target
            || _.find(customerAddresses, { id: activeAddressId })
            || _.find(customerAddresses, 'primary')
            || customerAddresses[0]
        const point = pointOf(addr)
        if (point) setMapCenter(point)
    }

    const handleClose = () => {
        setIsAddingGeofence(false)
        setGeoDrawPath([])
        setPendingAddress(null)
        onClose()
    }

    // Elegir la direccion nueva la tiene que mostrar: sin esto el mapa se quedaba en la
    // anterior, con esa geocerca resaltada, y parecia que el cambio no habia pasado.
    const selectPending = () => {
        setSelectedId(NEW_ID)
        setActiveAddressId(null)
        const first = pendingAddress?.hull?.[0]
        if (first) setMapCenter({ lat: first.lat, lng: first.lng })
    }

    const selectOrderOnly = () => {
        setSelectedId(ORDER_ID)
        setActiveAddressId(null)
        const point = pointOf(orderOnlyLocation)
        if (point) setMapCenter(point)
    }

    const discardPending = () => {
        setPendingAddress(null)
        setSelectedId(stopAddressId)
        clearLocationSearch()
    }

    const hasAddresses = customerAddresses.length > 0

    const searchBox = (
        <Div sx={{ mb: 2 }}>
            <Typography sx={{mb: 1}}>{stop ? 'Buscar nueva dirección:' : 'Buscar ubicación:'}</Typography>
            <Autocomplete onPlaceChanged={onPlaceChanged} onLoad={(autocomplete) => setSearchResult(autocomplete)}>
                <input
                    type="text"
                    ref={searchInputRef}
                    placeholder="Ingresa una dirección o lugar"
                    style={{
                        boxSizing: `border-box`,
                        border: `1px solid transparent`,
                        height: `36px`,
                        padding: `0 12px`,
                        borderRadius: `3px`,
                        boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
                        fontSize: `14px`,
                        outline: `none`,
                        textOverflow: `ellipsis`,
                        width: '100%',
                    }}
                />
            </Autocomplete>
            {stop && !pendingAddress && !isAddingGeofence &&
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Al elegirla queda lista para guardar, con una geocerca de {AUTO_GEOFENCE_RADIUS_M} m alrededor.
                </Typography>
            }
        </Div>
    )

    return(
        <Dialog
            fullWidth={true}
            maxWidth={"lg"}
            open={open}
            onClose={handleClose}>
            <DialogTitle>
                <Stack
                    direction="column"
                    justifyContent="start"
                    alignItems="left"
                    spacing={0}>
                    <Typography variant={"overline"} color="primary">{customer?.code}</Typography>
                    <Typography variant={"button"} color="primary">{customer?.name}</Typography>
                    {stop?.label &&
                        <Typography variant="caption" color="text.secondary">{stop.label}</Typography>
                    }
                </Stack>
            </DialogTitle>
            <DialogContent>
                { !isLoaded &&
                    <Div sx={{textAlign: 'center', my: 5}}>
                        <CircularProgress color="warning"/>
                    </Div>
                }
                { isLoaded &&
                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={8}>
                            <GoogleMap
                                mapContainerStyle={{ width: '100%', height: "400px" }}
                                center={mapCenter}
                                onClick={handleGeofenceMapClick}
                                // Mientras se dibuja, un click sobre un comercio o una estacion de Google
                                // abria su ficha en vez de sumar el vertice.
                                options={{ draggableCursor: isAddingGeofence ? 'crosshair' : undefined, clickableIcons: !isAddingGeofence }}
                                zoom={16}>
                                {/* Sin geocerca dibujada la direccion igual se ve, como pin. */}
                                {_.map(customerAddresses, (addr, idx) => {
                                    if (addr.path || !addr.lat || !addr.lng) return null
                                    return (
                                        <Marker
                                            key={`addr-pin-${addr.id}`}
                                            position={{ lat: _.toNumber(addr.lat), lng: _.toNumber(addr.lng) }}
                                            label={{ text: `${idx + 1}`, color: 'white', fontSize: '12px' }}
                                            onClick={() => setActiveAddressId(addr.id)}
                                        />
                                    )
                                })}
                                {_.map(customerAddresses, (addr) => {
                                    if (!addr.path) return null
                                    const isPrimary = addr.primary
                                    const isActive = addr.id === activeAddressId
                                    return (
                                        <Polygon
                                            key={`addr-${addr.id}`}
                                            path={geoCoords(addr.path)}
                                            onClick={() => setActiveAddressId(addr.id)}
                                            options={{
                                                // Mientras se dibuja, un click sobre otra geocerca tiene que
                                                // llegar al mapa como vertice, no seleccionarla.
                                                clickable: !isAddingGeofence,
                                                strokeColor: isActive ? "#199ee0" : (isPrimary ? "#FF0000" : "#8e24aa"),
                                                strokeOpacity: 0.9,
                                                strokeWeight: isActive ? 3 : 2,
                                                fillColor: isPrimary ? "#FF0000" : "#8e24aa",
                                                fillOpacity: isActive ? 0.4 : 0.25
                                            }}
                                        />
                                    )
                                })}
                                {orderOnlyLocation?.path &&
                                    <Polygon
                                        path={geoCoords(orderOnlyLocation.path)}
                                        onClick={() => { if (!isAddingGeofence) selectOrderOnly() }}
                                        options={{
                                            strokeColor: "#00897b",
                                            strokeOpacity: 0.9,
                                            strokeWeight: selectedId === ORDER_ID ? 3 : 2,
                                            fillColor: "#00897b",
                                            fillOpacity: selectedId === ORDER_ID ? 0.4 : 0.2,
                                            clickable: !isAddingGeofence,
                                        }}
                                    />
                                }
                                {orderOnlyLocation && !orderOnlyLocation.path && orderOnlyLocation.lat && orderOnlyLocation.lng &&
                                    <Marker position={{ lat: _.toNumber(orderOnlyLocation.lat), lng: _.toNumber(orderOnlyLocation.lng) }} onClick={selectOrderOnly} />
                                }
                                {pendingAddress &&
                                    <Polygon
                                        path={pendingAddress.hull}
                                        onClick={() => { if (stop && !isAddingGeofence) selectPending() }}
                                        options={{
                                            strokeColor: "#2e7d32",
                                            strokeOpacity: 0.9,
                                            strokeWeight: selectedId === NEW_ID ? 3 : 2,
                                            fillColor: "#2e7d32",
                                            fillOpacity: selectedId === NEW_ID ? 0.4 : 0.2,
                                            clickable: Boolean(stop) && !isAddingGeofence,
                                        }}
                                    />
                                }
                                {showPins && _.map(receiptsPins, (p, idx) => (
                                    <React.Fragment key={`receipt-pin-${idx}`}>
                                        <Marker position={{lat: _.toNumber(p.lat), lng: _.toNumber(p.lng)}}></Marker>
                                        <OverlayView
                                            position={{lat: _.toNumber(p.lat), lng: _.toNumber(p.lng)}}
                                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                            >
                                                <div style={{
                                                    background: `#203254`,
                                                    padding: `4px 8px`,
                                                    fontSize: '12px',
                                                    color: `white`,
                                                    borderRadius: '4px',
                                                    }}>
                                                    {moment(p.completed_at).format('DD MMM HH:mm')}
                                                </div>
                                        </OverlayView>
                                    </React.Fragment>
                                ))}
                                { tempPin && <Marker
                                    key={`preview_pin`}
                                    label={{
                                        text: `i`,
                                        color: 'white',
                                        fontSize: '14px'
                                    }}
                                    position={tempPin}
                                    icon={{
                                        path: "M0 -20C-3.87 -20 -7 -16.87 -7 -13c0 5.25 7 13 7 13s7 -7.75 7 -13c0 -3.87 -3.13 -7 -7 -7z",
                                        strokeColor: 'blue',
                                        fillOpacity: 1,
                                        fillColor: 'blue',
                                        scale: 1.3,
                                        labelOrigin: new window.google.maps.Point(0, -12),
                                    }}
                                ></Marker>}
                                {isAddingGeofence && geoDrawPath.length > 0 &&
                                    <React.Fragment>
                                        {geoDrawPath.length >= 3 &&
                                            <Polygon
                                                path={convexHull(geoDrawPath)}
                                                options={{ fillColor: "#199ee0", fillOpacity: 0.2, strokeColor: "#113460", strokeWeight: 2, clickable: false }}
                                            />
                                        }
                                        <Polyline
                                            path={geoDrawPath}
                                            options={{ strokeColor: "#113460", strokeWeight: 1, strokeOpacity: 0.4, clickable: false }}
                                        />
                                        {geoDrawPath.map((pt, idx) => (
                                            <Marker
                                                key={`geo_vertex_${idx}`}
                                                position={pt}
                                                icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 5, fillColor: idx === 0 ? "#ff5252" : "#113460", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 1 }}
                                                onClick={() => { if (idx === 0 && geoDrawPath.length >= 3) finishGeofence(); }}
                                            />
                                        ))}
                                    </React.Fragment>
                                }
                            </GoogleMap>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            {/* En el instructivo el buscador va primero: es el camino normal para
                                cambiar la direccion de la parada. */}
                            {stop && searchBox}

                            {isAddingGeofence &&
                                <Alert severity="info" icon={false} sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        Haz click en el mapa en al menos 3 puntos alrededor del lugar.
                                        {geoDrawPath.length < 3 && <> Faltan <strong>{3 - geoDrawPath.length}</strong>.</>}
                                    </Typography>
                                    <Typography variant="caption" color={searchedAddress ? 'success.main' : 'warning.main'} sx={{ display: 'block', mb: 1 }}>
                                        { searchedAddress
                                            ? <>Se guardará como: <strong>{searchedAddress.address}</strong></>
                                            : 'Busca la dirección arriba para guardarla junto a la geocerca. Si no, se usará la dirección de la primera esquina que dibujes.' }
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button size="small" variant="contained" color="success" disabled={geoDrawPath.length < 3} onClick={finishGeofence} sx={{ whiteSpace: 'nowrap' }}>
                                            Listo ({geoDrawPath.length} puntos)
                                        </Button>
                                        <Button size="small" color="error" onClick={cancelGeofence} sx={{ whiteSpace: 'nowrap' }}>Cancelar dibujo</Button>
                                    </Stack>
                                </Alert>
                            }

                            {loading && !hasAddresses &&
                                <Div sx={{textAlign: 'center', my: 2}}>
                                    <CircularProgress size={24} />
                                </Div>
                            }

                            {!loading && !hasAddresses && !pendingAddress && !orderOnlyLocation &&
                                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                                    {stop
                                        ? 'El cliente no tiene direcciones. Busca la ubicación arriba.'
                                        : 'El cliente no tiene direcciones. Busca la ubicación para dibujar su geocerca.'}
                                </Typography>
                            }

                            {stop && (hasAddresses || pendingAddress || orderOnlyLocation) &&
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Dirección para esta parada:</Typography>
                            }
                            {(hasAddresses || pendingAddress || orderOnlyLocation) &&
                                <List dense sx={{ mb: 2, border: '1px solid #eee', borderRadius: 1 }}>
                                    {pendingAddress &&
                                        <ListItem disablePadding selected={selectedId === NEW_ID} sx={{ pr: 1 }}>
                                            <Radio size="small" checked={selectedId === NEW_ID} disabled={saving} onChange={selectPending} />
                                            <ListItemButton onClick={selectPending} sx={{ pl: 0, minWidth: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <Typography variant="body2" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                                                    Nueva dirección{pendingAddress.address?.address ? ` — ${pendingAddress.address.address}` : ''}
                                                </Typography>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                                    <Chip size="small" color="warning" variant="outlined" label="Sin guardar" />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {pendingAddress.auto ? `Geocerca automática de ${AUTO_GEOFENCE_RADIUS_M} m` : 'Geocerca dibujada a mano'}
                                                    </Typography>
                                                </Stack>
                                            </ListItemButton>
                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                                <Tooltip title={pendingAddress.auto ? 'Ajustar geocerca' : 'Redibujar geocerca'}>
                                                    <span>
                                                        <IconButton size="small" color="primary" onClick={startAddGeofence} disabled={saving || isAddingGeofence}>
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <TrashIconButton
                                                    title="Descartar esta dirección"
                                                    onClick={discardPending}
                                                    disabled={saving || isAddingGeofence}
                                                />
                                            </Stack>
                                        </ListItem>
                                    }
                                    {orderOnlyLocation &&
                                        <ListItem disablePadding selected={selectedId === ORDER_ID} sx={{ pr: 1 }}>
                                            <Radio size="small" checked={selectedId === ORDER_ID} disabled={saving} onChange={selectOrderOnly} />
                                            <ListItemButton onClick={selectOrderOnly} sx={{ pl: 0, minWidth: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <Typography variant="body2" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                                                    Dirección del pedido{(orderOnlyLocation.short_address || orderOnlyLocation.address) ? ` — ${orderOnlyLocation.short_address || orderOnlyLocation.address}` : ''}
                                                </Typography>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                                    <Chip size="small" color="success" label="Actual de la parada" />
                                                    <Chip size="small" variant="outlined" label="Solo este pedido" />
                                                </Stack>
                                            </ListItemButton>
                                        </ListItem>
                                    }
                                    {_.map(customerAddresses, (addr, idx) => {
                                        const isActive = addr.id === activeAddressId
                                        const isStopAddress = Boolean(stop) && addr.location_id === stop.locationId
                                        return (
                                            // Las acciones van como hermano en flex y no como secondaryAction:
                                            // ese se posiciona en absoluto, así que la dirección no le reservaba
                                            // ancho y las más largas terminaban por debajo del chip "Principal".
                                            <ListItem
                                                key={`addr-item-${addr.id}`}
                                                selected={isActive}
                                                disablePadding
                                                sx={{ pr: 1 }}
                                            >
                                                {stop &&
                                                    <Radio
                                                        size="small"
                                                        checked={selectedId === addr.id}
                                                        disabled={!addr.lat || !addr.lng || saving}
                                                        onChange={() => { setSelectedId(addr.id); setActiveAddressId(addr.id); setCentreOnCustomer(addr); }}
                                                    />
                                                }
                                                <ListItemButton
                                                    onClick={() => {
                                                        setActiveAddressId(addr.id); setCentreOnCustomer(addr);
                                                        if (stop && addr.lat && addr.lng) setSelectedId(addr.id);
                                                    }}
                                                    sx={{ pl: stop ? 0 : 1, minWidth: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                                                    <Typography variant="body2" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                                                        {`Dirección ${idx + 1}`}{addr.short_address ? ` — ${addr.short_address}` : ''}
                                                    </Typography>
                                                    {isStopAddress && <Chip size="small" color="success" label="Actual de la parada" sx={{ mt: 0.5 }} />}
                                                </ListItemButton>
                                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                                    {addr.primary
                                                        ? <PrimaryAddressChip />
                                                        // En el instructivo la principal se cambia con el checkbox de abajo.
                                                        : !stop && <Tooltip title="Hacer esta geocerca la principal">
                                                              <IconButton size="small" color="warning" onClick={() => makeAddressPrimary(addr.id)}>
                                                                  <StarOutlineOutlinedIcon fontSize="small" />
                                                              </IconButton>
                                                          </Tooltip>
                                                    }
                                                    <TrashIconButton
                                                        title="Eliminar dirección y geocerca"
                                                        onClick={() => setConfirmRemoveAddress({ ...addr, index: idx })}
                                                    />
                                                </Stack>
                                            </ListItem>
                                        )
                                    })}
                                </List>
                            }

                            {stop && selectedId !== null &&
                                <Stack spacing={0} sx={{ mt: -1, mb: 2 }}>
                                    <FormControlLabel
                                        disabled={saving}
                                        control={<Checkbox size="small" checked={useForStop} onChange={(e) => setUseForStop(e.target.checked)} />}
                                        label={<Typography variant="body2">Usar para este pedido</Typography>}
                                    />
                                    <FormControlLabel
                                        disabled={saving || !canSaveAsPrimary}
                                        control={<Checkbox size="small" checked={canSaveAsPrimary ? markPrimary : Boolean(selectedAddress?.primary)} onChange={(e) => setMarkPrimary(e.target.checked)} />}
                                        label={<Typography variant="body2">
                                            {selectedAddress?.primary ? 'Ya es la dirección principal del cliente' : 'Asignar dirección al cliente'}
                                        </Typography>}
                                    />
                                </Stack>
                            }

                            {!stop && searchBox}

                            <ButtonGroup orientation="vertical" size="small" sx={{width: '100%', mt: stop ? 0 : 2}}>
                                <Button
                                    color="primary"
                                    disabled={receiptsPins.length === 0}
                                    onClick={() => { setShowPins(!showPins) }}>
                                    { (showPins && receiptsPins.length > 0) ?
                                        <React.Fragment><LocationOffOutlinedIcon /> Esconder pins de entregas ({receiptsPins.length})</React.Fragment>
                                    :
                                        <React.Fragment><PlaceOutlinedIcon /> Mostrar pins de entregas ({receiptsPins.length})</React.Fragment>
                                    }
                                </Button>
                                <Button
                                    color="primary"
                                    disabled={!hasAddresses}
                                    onClick={() => { setCentreOnCustomer(); }}>
                                    <MyLocationOutlinedIcon /> Centrar geocerca
                                </Button>
                                {/* En el instructivo la geocerca a mano se dibuja con el lapiz de la direccion nueva.
                                    En Clientes el boton aparece recien con una direccion buscada: sin ella la
                                    geocerca quedaba con la direccion de la primera esquina dibujada. */}
                                {!stop && searchedAddress && !isAddingGeofence &&
                                    <Button
                                        color="success"
                                        disabled={isAddingGeofence || loading || saving}
                                        onClick={startAddGeofence}>
                                        <ControlPointOutlinedIcon /> Agregar otra geocerca
                                    </Button>
                                }
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                }
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
                {stop ? (
                    <React.Fragment>
                        <Stack sx={{ flex: 1, minWidth: 260 }} spacing={0}>
                            <Typography variant="caption" color={canSaveStop || saving ? 'text.secondary' : 'warning.main'}>
                                {stopHint}
                            </Typography>
                        </Stack>
                        <Button onClick={handleClose} disabled={saving}>Cancelar</Button>
                        <Button variant="contained" disabled={!canSaveStop} onClick={() => saveForStop(wantsPrimary)}>
                            {saving ? 'Guardando…' : 'Guardar'}
                        </Button>
                    </React.Fragment>
                ) : (
                    <Button variant="contained" onClick={handleClose}>Cerrar</Button>
                )}
            </DialogActions>

            <ConfirmDialog
                open={Boolean(confirmRemoveAddress)}
                title="Eliminar dirección y geocerca"
                message={
                    <>
                        ¿Estás seguro que deseas eliminar la <strong>Dirección {(confirmRemoveAddress?.index ?? 0) + 1}</strong>{confirmRemoveAddress?.short_address ? <> — {confirmRemoveAddress.short_address}</> : null}?
                        <br />
                        Se borra también su geocerca y su horario preferente.
                    </>
                }
                loading={loading}
                onClose={() => setConfirmRemoveAddress(null)}
                onConfirm={() => {
                    removeCustomerAddress(confirmRemoveAddress.id);
                    setConfirmRemoveAddress(null);
                }}
            />
        </Dialog>
    )
}

export default LocationDialog;
